import React, { useEffect, useRef, useState } from "react";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Tooltip } from "antd";
import { all_routes } from "../../Router/all_routes";
// import { Settings, User } from "feather-icons-react/build/IconComponents";
import { getUserProfile } from "../../api/userApi";
import LanguageSwitcher from "../../InitialPage/Sidebar/LanguageSwitcher";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../api/authApi";
import * as XLSX from "xlsx";
import { getPosOverviewReport } from "../../api/transactionReportApi";

// ---- helper: convert catalog key -> public URL ----
const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v; // already a full URL
  const base = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");
  const key = String(v).replace(/^\/+/, "");
  return `${base}/${key}`;
};

const DEFAULT_REPORT_LIMIT = 100;
const DEFAULT_REPORT_TYPE = "cash";
const MAX_PDF_ORDERS = 25;
const POS_AVAILABILITY_EVENT = "pos:product-availability";

const toSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toYmd = (dateLike) => {
  const date = new Date(dateLike);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toAmount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const toInt = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : 0;
};

const normalizeName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.en || value.ar || "";
  return String(value);
};

const triggerBlobDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

const sanitizePdfLine = (line = "") =>
  String(line)
    .replace(/[^\x20-\x7E]/g, "?")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const buildSimplePdfBlob = (lines = []) => {
  const safeLines = (lines.length ? lines : ["No data"]).map(sanitizePdfLine);
  const commands = ["BT", "/F1 10 Tf", "40 800 Td", "14 TL"];

  safeLines.forEach((line, index) => {
    commands.push(index === 0 ? `(${line}) Tj` : `T* (${line}) Tj`);
  });
  commands.push("ET");

  const content = commands.join("\n");
  const objects = {
    1: "<< /Type /Catalog /Pages 2 0 R >>",
    2: "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    3:
      "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>",
    4: "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
    5: `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
  };

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let i = 1; i <= 5; i += 1) {
    offsets[i] = pdf.length;
    pdf += `${i} 0 obj\n${objects[i]}\nendobj\n`;
  }

  const xrefStart = pdf.length;
  pdf += "xref\n0 6\n";
  pdf += "0000000000 65535 f \n";
  for (let i = 1; i <= 5; i += 1) {
    pdf += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += "trailer\n<< /Size 6 /Root 1 0 R >>\n";
  pdf += `startxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdf], { type: "application/pdf" });
};

const buildReportParams = (userId) => {
  const to = toYmd(new Date());
  const firstDayOfMonth = new Date();
  firstDayOfMonth.setDate(1);

  return {
    from: toYmd(firstDayOfMonth),
    to,
    userId: userId || undefined,
    limit: DEFAULT_REPORT_LIMIT,
    type: DEFAULT_REPORT_TYPE,
  };
};

const downloadOverviewExcel = (report, params) => {
  const summary = report?.summary || {};
  const summaryRows = [
    { Metric: "Total Transactions", Value: toInt(summary.totalTransactions) },
    { Metric: "Total Sales Amount", Value: Number(toAmount(summary.totalSalesAmount)) },
    { Metric: "Total Received Amount", Value: Number(toAmount(summary.totalReceivedAmount)) },
    { Metric: "Total Items Sold", Value: toInt(summary.totalItemsSold) },
    { Metric: "From", Value: params.from || "-" },
    { Metric: "To", Value: params.to || "-" },
    { Metric: "Payment Type", Value: params.type || "-" },
  ];

  const orderRows = (report?.orders || []).map((order, index) => {
    const sellerName = [normalizeName(order?.seller?.firstName), normalizeName(order?.seller?.lastName)]
      .join(" ")
      .trim();

    return {
      Sr: index + 1,
      OrderId: order?.orderId || "-",
      TransactionId: order?.transactionId || "-",
      Type: order?.type || "-",
      TotalAmount: Number(toAmount(order?.totalAmount)),
      ReceivedAmount: Number(toAmount(order?.receivedAmount)),
      ItemsSold: toInt(order?.itemsSold),
      Seller: sellerName || order?.seller?.username || "-",
      CreatedAt: order?.createdAt ? new Date(order.createdAt).toLocaleString() : "-",
    };
  });

  const userRows = (report?.userSales || []).map((user, index) => ({
    Sr: index + 1,
    UserId: user?.userId || "-",
    Name: [normalizeName(user?.firstName), normalizeName(user?.lastName)].join(" ").trim() || "-",
    Username: user?.username || "-",
    Role: user?.role || "-",
    TotalTransactions: toInt(user?.totalTransactions),
    TotalSalesAmount: Number(toAmount(user?.totalSalesAmount)),
    TotalItemsSold: toInt(user?.totalItemsSold),
  }));

  const productRows = (report?.productSales || []).map((product, index) => ({
    Sr: index + 1,
    ProductId: product?.productId || "-",
    Name: product?.name || "-",
    TotalQuantitySold: toInt(product?.totalQuantitySold),
    TotalSalesAmount: Number(toAmount(product?.totalSalesAmount)),
    TransactionCount: toInt(product?.transactionCount),
    SellerCount: toInt(product?.sellerCount),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), "Summary");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(orderRows), "Orders");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(userRows), "UserSales");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(productRows), "ProductSales");
  XLSX.writeFile(wb, `pos_overview_${params.from}_to_${params.to}.xlsx`);
};

const downloadOverviewPdf = (report, params) => {
  const summary = report?.summary || {};
  const lines = [
    "POS Selling Overview Report",
    `From: ${params.from || "-"}`,
    `To: ${params.to || "-"}`,
    `Type: ${params.type || "-"}`,
    "",
    "Summary",
    `Total Transactions: ${toInt(summary.totalTransactions)}`,
    `Total Sales Amount: ${toAmount(summary.totalSalesAmount)}`,
    `Total Received Amount: ${toAmount(summary.totalReceivedAmount)}`,
    `Total Items Sold: ${toInt(summary.totalItemsSold)}`,
    "",
    "Recent Orders",
  ];

  const orders = report?.orders || [];
  if (!orders.length) {
    lines.push("No orders found for this range.");
  } else {
    orders.slice(0, MAX_PDF_ORDERS).forEach((order, idx) => {
      const seller = [
        normalizeName(order?.seller?.firstName),
        normalizeName(order?.seller?.lastName),
      ]
        .join(" ")
        .trim();
      const sellerText = seller || order?.seller?.username || "-";
      const orderLabel = order?.orderId || order?.transactionId || "-";
      lines.push(
        `${idx + 1}. ${orderLabel} | ${order?.type || "-"} | Total ${toAmount(
          order?.totalAmount
        )} | Items ${toInt(order?.itemsSold)} | Seller ${sellerText}`
      );
    });
  }

  const pdfBlob = buildSimplePdfBlob(lines);
  triggerBlobDownload(pdfBlob, `pos_overview_${params.from}_to_${params.to}.pdf`);
};

const PosHeader = () => {
  const route = all_routes;
  const t = (key) => (window?.translate ? window.translate(key) : key);
  const location = useLocation();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [profileImage, setProfileImage] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userId, setUserId] = useState("");
  const [reportLoading, setReportLoading] = useState("");
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [availabilitySnapshot, setAvailabilitySnapshot] = useState(null);
  const [availabilityHasCartItems, setAvailabilityHasCartItems] = useState(false);
  const availabilityCartProductIdsRef = useRef([]);
  const availabilityPendingProductIdRef = useRef("");

  const navigate = useNavigate();
  const posLayoutOptions = [
    { key: "pos1", path: route.pos, label: window.translate("POS Layout 1") },
    { key: "pos2", path: route.pos2, label: window.translate("POS Layout 2") },
    // { key: "pos3", path: route.pos3, label: window.translate("POS Layout 3") },
    // { key: "pos4", path: route.pos4, label: window.translate("POS Layout 4") },
    // { key: "pos5", path: route.pos5, label: window.translate("POS Layout 5") },
    // Hide unavailable layout options by filtering out routes with no path.
  ].filter((layout) => Boolean(layout.path));

  const currentLayoutPath =
    posLayoutOptions.find((layout) => layout.path === location.pathname)?.path ||
    route.pos;

  const handleLayoutSwitch = (path) => {
    if (!path || path === location.pathname) return;
    navigate(path);
  };

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate("/pos/signin", { replace: true });
  };

  const toggleFullscreen = (elem) => {
    elem = elem || document.documentElement;
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      const result = await getUserProfile();
      if (result.success) {
        const raw =
          result.data.profileImageUrl || result.data.profileImage || "";
        setProfileImage(raw ? toPublicUrl(raw) : "");
        setFirstName(result.data.firstName || "");
        setLastName(result.data.lastName || "");
        setUserId(result.data._id || result.data.id || "");
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // update every second

    return () => clearInterval(interval); // cleanup on unmount
  }, []);

  useEffect(() => {
    const handleAvailabilityEvent = (event) => {
      const payload = event?.detail || {};

      if (payload.kind === "cart-sync") {
        const productIds = Array.isArray(payload.productIds)
          ? payload.productIds.map((id) => String(id))
          : [];
        availabilityCartProductIdsRef.current = productIds;
        setAvailabilityHasCartItems(productIds.length > 0);

        if (productIds.length === 0) {
          setAvailabilityLoading(false);
          setAvailabilityError("");
          setAvailabilitySnapshot(null);
          availabilityPendingProductIdRef.current = "";
          return;
        }

        setAvailabilityLoading(false);
        setAvailabilityError("");
        setAvailabilitySnapshot((prev) => {
          if (!prev?.productId) return prev;
          return productIds.includes(String(prev.productId)) ? prev : null;
        });
        return;
      }

      if (payload.loading) {
        setAvailabilityLoading(true);
        setAvailabilityError("");
        availabilityPendingProductIdRef.current = String(payload.productId || "");
        setAvailabilitySnapshot((prev) => ({
          ...(prev || {}),
          productId: payload.productId || prev?.productId || "",
          productName:
            normalizeName(payload.productName) ||
            normalizeName(prev?.productName) ||
            "",
        }));
        return;
      }

      setAvailabilityLoading(false);
      const incomingProductId = String(payload.productId || "");
      const isPendingProduct =
        incomingProductId &&
        availabilityPendingProductIdRef.current === incomingProductId;
      const isInCart =
        incomingProductId &&
        availabilityCartProductIdsRef.current.includes(incomingProductId);
      if (
        incomingProductId &&
        !isPendingProduct &&
        !isInCart
      ) {
        return;
      }

      if (payload.error) {
        availabilityPendingProductIdRef.current = "";
        setAvailabilityError(payload.message || t("Availability unavailable"));
        setAvailabilitySnapshot((prev) => ({
          ...(prev || {}),
          productId: payload.productId || prev?.productId || "",
          productName:
            normalizeName(payload.productName) ||
            normalizeName(prev?.productName) ||
            "",
        }));
        return;
      }

      const payloadData = payload.data || {};
      availabilityPendingProductIdRef.current = "";
      setAvailabilityError("");
      setAvailabilitySnapshot({
        productId: payload.productId || "",
        productName: normalizeName(payload.productName) || "",
        store: normalizeName(payloadData.store || payloadData.storeName) || "",
        storeId: payloadData.storeId || "",
        warehouse:
          normalizeName(payloadData.warehouse || payloadData.warehouseName) || "",
        warehouseId: payloadData.warehouseId || "",
        availability: Array.isArray(payloadData.availability)
          ? payloadData.availability
          : [],
      });
    };

    window.addEventListener(POS_AVAILABILITY_EVENT, handleAvailabilityEvent);
    return () => {
      window.removeEventListener(POS_AVAILABILITY_EVENT, handleAvailabilityEvent);
    };
  }, []);

  useEffect(() => {
    if (!isAvailabilityModalOpen) return undefined;
    document.body.classList.add("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [isAvailabilityModalOpen]);

  const formattedTime = currentTime.toLocaleTimeString("en-US", {
    hour12: false, // set to true for AM/PM
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const getAvailabilityStatus = (quantity, quantityAlert) => {
    const qty = toSafeNumber(quantity);
    const alertQty = toSafeNumber(quantityAlert);
    if (qty <= 0) {
      return {
        label: t("Out of Stock"),
        className: "badge bg-danger-subtle text-danger border border-danger-subtle",
      };
    }
    if (qty > 0 && qty <= alertQty) {
      return {
        label: t("Low Stock"),
        className: "badge bg-warning-subtle text-warning border border-warning-subtle",
      };
    }
    return {
      label: t("In Stock"),
      className: "badge bg-success-subtle text-success border border-success-subtle",
    };
  };

  const handleReportDownload = async (format) => {
    if (reportLoading) return;

    setReportLoading(format);
    try {
      const params = buildReportParams(userId);
      const report = await getPosOverviewReport(params);

      if (!report?.success) {
        throw new Error(report?.message || window.translate("Failed to fetch report data."));
      }

      if (format === "excel") {
        downloadOverviewExcel(report, params);
      } else {
        downloadOverviewPdf(report, params);
      }
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          error?.message ||
          window.translate("Failed to download report. Please try again.")
      );
    } finally {
      setReportLoading("");
    }
  };

  return (
    <>
      {/* Header */}
      <div className="header pos-header">
        {/* Logo */}
        <div className="header-left active">
          <Link to={all_routes.newdashboard} className="logo logo-normal">
            <ImageWithBasePath src="assets/img/logo.png" alt="Img" />
          </Link>
          <Link to={all_routes.newdashboard} className="logo logo-white">
            <ImageWithBasePath src="assets/img/logo-white.png" alt="Img" />
          </Link>
          <Link to={all_routes.newdashboard} className="logo-small">
            <ImageWithBasePath src="assets/img/logo-small.png" alt="Img" />
          </Link>
        </div>
        {/* /Logo */}
        <Link id="mobile_btn" className="mobile_btn d-none" to="#sidebar">
          <span className="bar-icon">
            <span />
            <span />
            <span />
          </span>
        </Link>
        {/* Header Menu */}
        <ul className="nav user-menu">
          {/* Search */}
          <li className="nav-item time-nav">
            <span className="bg-teal text-white d-inline-flex align-items-center">
              <ImageWithBasePath
                src="assets/img/icons/clock-icon.svg"
                alt="img"
                className="me-2 ms-2"
              />
              {formattedTime}
            </span>
          </li>
          {/* /Search */}
          {/* <li className="nav-item pos-nav">
            <Link
              to={all_routes.newdashboard}
              className="btn btn-purple btn-md d-inline-flex align-items-center"
            >
              <i className="ti ti-world me-1" />
              {window.translate("Dashboard")}
            </Link>
          </li> */}
          {/* Select Store */}
          {/* <li className="nav-item dropdown has-arrow main-drop select-store-dropdown">
                        <Link
                            to="#"
                            className="dropdown-toggle nav-link select-store"
                            data-bs-toggle="dropdown"
                        >
                            <span className="user-info">
                                <span className="user-letter">
                                    <ImageWithBasePath
                                        src="assets/img/store/store-01.png"
                                        alt="Store Logo"
                                        className="img-fluid"
                                    />
                                </span>
                                <span className="user-detail">
                                    <span className="user-name">Freshmart</span>
                                </span>
                            </span>
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                            <Link to="#" className="dropdown-item">
                                <ImageWithBasePath
                                    src="assets/img/store/store-01.png"
                                    alt="Store Logo"
                                    className="img-fluid"
                                />
                                Freshmart
                            </Link>
                            <Link to="#" className="dropdown-item">
                                <ImageWithBasePath
                                    src="assets/img/store/store-02.png"
                                    alt="Store Logo"
                                    className="img-fluid"
                                />
                                Grocery Apex
                            </Link>
                            <Link to="#" className="dropdown-item">
                                <ImageWithBasePath
                                    src="assets/img/store/store-03.png"
                                    alt="Store Logo"
                                    className="img-fluid"
                                />
                                Grocery Bevy
                            </Link>
                            <Link to="#" className="dropdown-item">
                                <ImageWithBasePath
                                    src="assets/img/store/store-04.png"
                                    alt="Store Logo"
                                    className="img-fluid"
                                />
                                Grocery Eden
                            </Link>
                        </div>
                    </li> */}
          {/* /Select Store */}
          <li className="nav-item nav-item-box">
            <Link
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#calculator"
              className="bg-orange border-orange text-white"
            >
              <i className="ti ti-calculator" />
            </Link>
          </li>

          <li className="nav-item nav-item-box">
            <Tooltip title={t("Product Availability")} placement="right">
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsAvailabilityModalOpen(true);
                }}
              >
                <i className="ti ti-info-circle" />
              </Link>
            </Tooltip>
          </li>

          {/* Lang */}
          <li className="nav-item-box">
            <LanguageSwitcher /> {/* no <Link> wrapper */}
          </li>

          <li className="nav-item dropdown nav-item-box">
            <Tooltip title={window.translate("Switch Layout")} placement="right">
              <Link
                to="#"
                className="dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-layout-grid" />
              </Link>
            </Tooltip>
            <div className="dropdown-menu dropdown-menu-end pos-layout-dropdown">
              <div className="dropdown-header">
                <h6 className="pos-layout-title">{window.translate("POS Layouts")}</h6>
                <p className="pos-layout-subtitle mb-0">
                  {window.translate("Choose POS Layout")}
                </p>
              </div>
              {posLayoutOptions.map((layout) => (
                <button
                  key={layout.key}
                  type="button"
                  className={`dropdown-item pos-layout-option d-flex align-items-center justify-content-between ${
                    currentLayoutPath === layout.path ? "active" : ""
                  }`}
                  onClick={() => handleLayoutSwitch(layout.path)}
                >
                  <span className="d-inline-flex align-items-center gap-2">
                    <span className="pos-layout-icon-wrap">
                      <i className="ti ti-layout-grid" />
                    </span>
                    <span className="pos-layout-label">{layout.label}</span>
                  </span>
                  {currentLayoutPath === layout.path && (
                    <span className="pos-layout-check" title={window.translate("Active")}>
                      <i className="ti ti-check" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </li>

          <li className="nav-item nav-item-box">
            <Tooltip title={window.translate("Maximize")} placement="right">
              <Link
                to="#"
                id="btnFullscreen"
                onClick={() => toggleFullscreen()}
                className={isFullscreen ? "Exit Fullscreen" : "Go Fullscreen"}
              >
                <i className="ti ti-maximize" />
              </Link>
            </Tooltip>
          </li>
          <li className="nav-item dropdown nav-item-box">
            <Tooltip title={window.translate("Download Report")} placement="right">
              <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                <i className="ti ti-download" />
              </Link>
            </Tooltip>
            <div className="dropdown-menu dropdown-menu-end">
              <button
                type="button"
                className="dropdown-item"
                disabled={!!reportLoading}
                onClick={() => handleReportDownload("pdf")}
              >
                <i className="ti ti-file-type-pdf me-2" />
                {reportLoading === "pdf"
                  ? window.translate("Downloading PDF...")
                  : window.translate("Download PDF")}
              </button>
              <button
                type="button"
                className="dropdown-item"
                disabled={!!reportLoading}
                onClick={() => handleReportDownload("excel")}
              >
                <i className="ti ti-file-spreadsheet me-2" />
                {reportLoading === "excel"
                  ? window.translate("Downloading Excel...")
                  : window.translate("Download Excel")}
              </button>
            </div>
          </li>
          {/* <li
                        className="nav-item nav-item-box"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-title="Cash Register"
                    >
                        <Tooltip title='Cash Register' placement='right'>
                            <Link to="#" data-bs-toggle="modal" data-bs-target="#cash-register">
                                <i className="ti ti-cash" />
                            </Link>
                        </Tooltip>

                    </li> */}
          {/* <li
            className="nav-item nav-item-box"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-title="Print Last Reciept"
          >
            <Tooltip title="Print Last Reciept" placement="right">
              <Link to="#">
                <i className="ti ti-printer" />
              </Link>
            </Tooltip>
          </li> */}
          {/* <li
                        className="nav-item nav-item-box"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-title="Today’s Sale"
                    >
                    <Tooltip title='Today&apos;s Sale' placement='right'>
                        <Link to="#" data-bs-toggle="modal" data-bs-target="#today-sale">
                            <i className="ti ti-progress" />
                        </Link>
                        </Tooltip>
                    </li> */}
          {/* <li
                        className="nav-item nav-item-box"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-title="Today’s Profit"
                    >
                    <Tooltip title='Today’s Profit' placement='right'>
                        <Link to="#" data-bs-toggle="modal" data-bs-target="#today-profit">
                            <i className="ti ti-chart-infographic" />
                        </Link>
                        </Tooltip>
                    </li> */}
          {/* <li
                        className="nav-item nav-item-box"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-title="POS Settings"
                    >
                    <Tooltip title='POS Settings' placement='bottom'>
                        <Link to={all_routes.possettings}>
                            <i className="ti ti-settings" />
                        </Link>
                        </Tooltip>
                    </li> */}
          <li className="nav-item dropdown has-arrow main-drop profile-nav">
            <a
              href="#"
              className="nav-link userset"
              data-bs-toggle="dropdown"
              aria-expanded="true"
            >
              <span className="user-info p-0">
                <span className="user-letter">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="img-fluid"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <ImageWithBasePath
                      src="assets/img/profiles/avator1.jpg"
                      alt="Default Avatar"
                      className="img-fluid"
                    />
                  )}
                </span>
              </span>
            </a>
            <div className="dropdown-menu menu-drop-user">
              <div className="profileset d-flex align-items-center">
                <span className="user-img me-2">
                  <span className="user-letter">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="img-fluid"
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <ImageWithBasePath
                        src="assets/img/profiles/avator1.jpg"
                        alt="Default Avatar"
                        className="img-fluid"
                      />
                    )}
                  </span>
                </span>
                <div>
                  <h6 className="fw-medium">
                    {" "}
                    {firstName} {lastName}
                  </h6>
                  {/* <p>Admin</p> */}
                </div>
              </div>
              <Link className="dropdown-item" to={route.profile}>
                <i className="ti ti-user-circle me-2" />
                {window.translate("MyProfile")}
              </Link>
              {/* <Link className="dropdown-item" to={route.salesreport}>
                  <i className="ti ti-file-text me-2" />
                  Reports
                </Link> */}
              {/* <Link className="dropdown-item" to={route.generalsettings}>
                  <i className="ti ti-settings-2 me-2" />
                  Settings
                </Link> */}
              <hr className="my-2" />
              <Link
                className="dropdown-item logout pb-0"
                to="#"
                onClick={handleLogout}
              >
                <i className="ti ti-logout me-2" />
                {window.translate("Logout")}
              </Link>
            </div>
          </li>
        </ul>
        {/* /Header Menu */}
        {/* Mobile Menu */}
        <div className="dropdown mobile-user-menu">
          <Link
            to="#"
            className="nav-link dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
          </Link>

          <div className="dropdown-menu dropdown-menu-right pos-layout-dropdown">
            <Link className="dropdown-item" to="profile">
              {window.translate("MyProfile")}
            </Link>

            {/* <Link
              to={all_routes.newdashboard}
              className="btn btn-md d-inline-flex align-items-center"
            >
              <i className="ti ti-world me-1" />
              {window.translate("Dashboard")}
            </Link> */}

            <button
              type="button"
              className="dropdown-item"
              disabled={!!reportLoading}
              onClick={() => handleReportDownload("pdf")}
            >
              {reportLoading === "pdf"
                ? window.translate("Downloading PDF...")
                : window.translate("Download PDF")}
            </button>
            <button
              type="button"
              className="dropdown-item"
              disabled={!!reportLoading}
              onClick={() => handleReportDownload("excel")}
            >
              {reportLoading === "excel"
                ? window.translate("Downloading Excel...")
                : window.translate("Download Excel")}
            </button>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => setIsAvailabilityModalOpen(true)}
            >
              {t("Product Availability")}
            </button>
            <hr className="dropdown-divider" />
            <div className="dropdown-header px-3">
              <h6 className="pos-layout-title mb-1">{window.translate("POS Layouts")}</h6>
              <p className="pos-layout-subtitle mb-0">
                {window.translate("Choose POS Layout")}
              </p>
            </div>
            {posLayoutOptions.map((layout) => (
              <button
                key={layout.key}
                type="button"
                className={`dropdown-item pos-layout-option d-flex align-items-center justify-content-between ${
                  currentLayoutPath === layout.path ? "active" : ""
                }`}
                onClick={() => handleLayoutSwitch(layout.path)}
              >
                <span className="d-inline-flex align-items-center gap-2">
                  <span className="pos-layout-icon-wrap">
                    <i className="ti ti-layout-grid" />
                  </span>
                  <span className="pos-layout-label">{layout.label}</span>
                </span>
                {currentLayoutPath === layout.path && (
                  <span className="pos-layout-check" title={window.translate("Active")}>
                    <i className="ti ti-check" />
                  </span>
                )}
              </button>
            ))}
            <Link className="dropdown-item" to="#" onClick={handleLogout}>
              {window.translate("Logout")}
            </Link>
            <Link className="dropdown-item mb-2">
              <LanguageSwitcher /> {/* no <Link> wrapper */}
            </Link>
          </div>
        </div>
        {/* /Mobile Menu */}
      </div>

      {isAvailabilityModalOpen && (
        <div
          className="modal fade show d-block pos-availability-modal"
          role="dialog"
          aria-modal="true"
          tabIndex="-1"
          onClick={() => setIsAvailabilityModalOpen(false)}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header pos-availability-modal__header">
                <div>
                  <h5 className="modal-title mb-1 d-flex align-items-center gap-2">
                    <span className="pos-availability-modal__title-icon">
                      <i className="ti ti-package" />
                    </span>
                    {t("Availability")}
                  </h5>
                  {availabilitySnapshot?.productName ? (
                    <p className="text-muted mb-0 small">
                      {t("Product")}: {availabilitySnapshot.productName}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="pos-availability-modal__close"
                  aria-label={t("Close")}
                  onClick={() => setIsAvailabilityModalOpen(false)}
                >
                  <i className="ti ti-x" />
                </button>
              </div>

              <div className="modal-body">
                {availabilityLoading ? (
                  <div className="d-flex align-items-center gap-2 py-2">
                    <span className="spinner-border spinner-border-sm" role="status" />
                    <span>{t("Loading...")}</span>
                  </div>
                ) : availabilityError ? (
                  <div className="alert alert-warning mb-0 rounded-3">
                    {t("Availability unavailable")}
                  </div>
                ) : !availabilitySnapshot?.productId ? (
                  <div className="pos-availability-modal__empty">
                    <span className="icon">
                      <i
                        className={`ti ${availabilityHasCartItems ? "ti-info-circle" : "ti-shopping-cart-off"}`}
                      />
                    </span>
                    <h6 className="mb-1">
                      {availabilityHasCartItems
                        ? t("No Product Selected")
                        : t("No Product In Cart")}
                    </h6>
                    <p className="mb-0 text-muted">
                      {availabilityHasCartItems
                        ? t("Select a product card to load availability details.")
                        : t("Add a product to cart, then open availability info.")}
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="availability-primary-line mb-3">
                      <span className="fw-semibold">{t("Store")}:</span>{" "}
                      {availabilitySnapshot.store || "-"},{" "}
                      <span className="fw-semibold">{t("Warehouse")}:</span>{" "}
                      {availabilitySnapshot.warehouse || "-"}
                    </p>

                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>{t("Store Name")}</th>
                            <th>{t("Warehouse Name")}</th>
                            <th className="text-end">{t("Quantity")}</th>
                            <th className="text-end">{t("Alert Qty")}</th>
                            <th>{t("Status")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(availabilitySnapshot.availability) &&
                          availabilitySnapshot.availability.length > 0 ? (
                            availabilitySnapshot.availability.map((item, index) => {
                              const status = getAvailabilityStatus(
                                item?.quantity,
                                item?.quantityAlert,
                              );
                              return (
                                <tr
                                  key={String(
                                    item?._id ||
                                      `${item?.storeId || "store"}-${item?.warehouseId || "wh"}-${index}`,
                                  )}
                                >
                                  <td>{normalizeName(item?.storeName) || "-"}</td>
                                  <td>{normalizeName(item?.warehouseName) || "-"}</td>
                                  <td className="text-end">
                                    {toSafeNumber(item?.quantity).toLocaleString()}
                                  </td>
                                  <td className="text-end">
                                    {toSafeNumber(item?.quantityAlert).toLocaleString()}
                                  </td>
                                  <td>
                                    <span className={status.className}>{status.label}</span>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-muted">
                                {t("No availability records found.")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pos-layout-dropdown {
          width: min(320px, calc(100vw - 24px));
          border: 1px solid #f2e3cd;
          border-radius: 14px;
          box-shadow: 0 14px 28px rgba(15, 23, 42, 0.14);
          padding: 10px;
          margin-top: 10px;
        }
        .pos-layout-dropdown .dropdown-header {
          border-bottom: 1px dashed #f0dfc4;
          margin: 0 2px 10px;
          padding: 0 2px 10px;
        }
        .pos-layout-title {
          font-size: 16px;
          font-weight: 800;
          color: #1f2937;
          margin: 0;
        }
        .pos-layout-subtitle {
          font-size: 12px;
          color: #6b7280;
          margin: 0;
        }
        .pos-layout-option {
          border: 1px solid #edf2f7;
          border-radius: 10px;
          margin-bottom: 8px;
          padding: 10px 12px;
          text-align: start;
          transition: all 0.2s ease;
        }
        .pos-layout-option:last-child {
          margin-bottom: 0;
        }
        .pos-layout-option:hover {
          border-color: #f3c98d;
          background: #fff8ee;
        }
        .pos-layout-option.active {
          border-color: #f39f1a;
          background: #fff2dc;
        }
        .pos-layout-icon-wrap {
          width: 24px;
          height: 24px;
          border-radius: 8px;
          background: #fff;
          border: 1px solid #ebedf0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          font-size: 14px;
        }
        .pos-layout-option.active .pos-layout-icon-wrap {
          color: #f39f1a;
          border-color: #f5d6a8;
        }
        .pos-layout-label {
          font-weight: 700;
          color: #111827;
        }
        .pos-layout-check {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #e8f8ef;
          color: #14a44d;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
        }
        .pos-availability-modal {
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(2px);
        }
        .pos-availability-modal .modal-content {
          border: 1px solid #ebedf1;
          border-radius: 16px;
          box-shadow: 0 24px 52px rgba(15, 23, 42, 0.22);
          overflow: hidden;
        }
        .pos-availability-modal .modal-header {
          border-bottom: 1px solid #eef1f5;
          background: linear-gradient(135deg, #fff9ef, #f9fbff);
        }
        .pos-availability-modal__header {
          padding: 16px 20px;
        }
        .pos-availability-modal__title-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          border: 1px solid #f5dcc0;
          background: #fff2df;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #c67812;
          font-size: 15px;
        }
        .pos-availability-modal__close {
          width: 34px;
          height: 34px;
          border-radius: 999px;
          border: 1px solid #f2d1ab;
          background: #fff4e7;
          color: #bc6b00;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          transition: all 0.2s ease;
        }
        .pos-availability-modal__close:hover {
          background: #ffe8cc;
          border-color: #efbf84;
        }
        .pos-availability-modal__close i {
          line-height: 1;
        }
        .pos-availability-modal__empty {
          border: 1px dashed #e1e7ef;
          border-radius: 12px;
          padding: 24px 16px;
          text-align: center;
          background: linear-gradient(180deg, #fbfdff, #f7fafc);
        }
        .pos-availability-modal__empty .icon {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          margin: 0 auto 12px;
          border: 1px solid #e6edf6;
          background: #eef4fb;
          color: #4b5b70;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }
        .pos-availability-modal .availability-primary-line {
          background: #f8fafc;
          border: 1px solid #e9edf3;
          border-radius: 10px;
          padding: 10px 12px;
        }
        .pos-availability-modal thead th {
          font-size: 12px;
          letter-spacing: 0.02em;
          color: #667085;
          text-transform: uppercase;
          border-bottom-color: #edf1f5;
        }
        .pos-availability-modal tbody td {
          border-bottom-color: #f1f5f9;
        }
        .pos-availability-modal tbody tr:hover td {
          background: #fbfdff;
        }
        @media (max-width: 575px) {
          .pos-layout-dropdown {
            min-width: 250px;
            padding: 8px;
          }
          .pos-layout-option {
            padding: 9px 10px;
          }
        }
      `}</style>
      {/* Header */}
    </>
  );
};

export default PosHeader;

